import React from "react";
import { useTranslation } from "react-i18next";
import { Star, ExternalLink, Quote } from "lucide-react";
import { testimonials } from "@/data/pricing";

const GOOGLE_REVIEW_URL = "https://g.page/r/CWM_ouLn_9JqEBM";

const GoogleG = ({ size = 22 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width={size} height={size} aria-hidden="true">
    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
  </svg>
);

const ReviewsSection = () => {
  const { t } = useTranslation();
  const picks = testimonials.slice(0, 3);

  return (
    <section data-testid="reviews-section" className="py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6 lg:px-12">
        <div className="flex flex-col items-center text-center max-w-2xl mx-auto mb-12">
          <div className="flex items-center gap-2 mb-4">
            <GoogleG size={24} />
            <span className="text-white/60 text-sm font-medium">Google</span>
          </div>
          <div className="flex items-center gap-3 mb-2">
            <span className="font-serif text-5xl md:text-6xl text-[#D4AF37] leading-none">5,0</span>
            <div className="flex flex-col items-start">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} size={18} className="fill-[#FBBC04] text-[#FBBC04]" />
                ))}
              </div>
              <span className="text-white/45 text-xs mt-1">{t("home.reviewsTitle")}</span>
            </div>
          </div>
          <div className="text-[#D4AF37] text-[11px] tracking-[0.3em] uppercase font-medium mt-3 mb-1">— {t("home.reviewsOverline")}</div>
          <p className="text-white/55 text-base md:text-lg">{t("home.reviewsSubtitle")}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-4 md:gap-5 max-w-5xl mx-auto mb-10">
          {picks.map((r, i) => (
            <div key={i} data-testid={`review-card-${i}`} className="bg-[#0F1115] border border-white/8 rounded-2xl p-6 flex flex-col">
              <Quote size={20} className="text-[#D4AF37]/50 mb-3" />
              <div className="flex mb-3">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star key={s} size={13} className="fill-[#FBBC04] text-[#FBBC04]" />
                ))}
              </div>
              <p className="text-white/70 text-sm leading-relaxed flex-1 line-clamp-5">{r.text}</p>
              <div className="mt-4 pt-4 border-t border-white/5">
                <div className="text-white text-sm font-medium">{r.name}</div>
                <div className="text-white/40 text-xs">{r.city}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-center">
          <a
            href={GOOGLE_REVIEW_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="reviews-google-btn"
            className="inline-flex items-center gap-2.5 bg-white hover:bg-white/90 text-[#1F1F1F] rounded-full pl-3 pr-6 py-2.5 text-sm font-semibold transition-all hover:scale-105 shadow-xl"
          >
            <GoogleG size={20} /> {t("home.reviewsCta")} <ExternalLink size={14} className="text-[#5F6368]" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;
